import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Ticket,
    Tag,
    Calendar,
    Shield,
    Percent,
    Hash,
    Users,
    Clock,
    PencilIcon,
    AlarmClock,
    Layers,
    Info,
    Trash2,
    Eye,
    ShoppingBag,
    User,
} from 'lucide-react';
import { Layout } from '@/components/Layout/Layout';
import { MainHeading } from '@/components/common/MainHeading';
import { SectionCard } from '@/components/common/SectionCard';
import { DetailItem } from '@/components/common/DetailItem';
import { StatusBadge } from '@/components/common/StatusBadge';
import { NoDataFound } from '@/components/common/NoDataFound';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { ICoupon, ISingleCouponResponse } from '@/interfaces/Coupon/coupon.interface';
import { formatDateTime, formatRand, showToastNotification } from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { useRoleNavigate } from '@/hooks/useRoleNavigate';
import ConfirmationModal from '@/components/common/ConfirmationModal';
import { RootState, useAppSelector } from '@/redux/store';
import { usePermissions } from '@/hooks/usePermissions';
import { RESOURCES } from '@/context/permissions';
import CommonTable, { TableColumn } from '@/components/common/CommonTable';
import Pagination from '@/components/common/Pagination';
import { IOrder, IOrderResponse } from '@/interfaces/Order/order.interface';
import { IPageInfo } from '@/interfaces/common.interface';

export const CouponView = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const roleNavigate = useRoleNavigate();
    const user = useAppSelector((state: RootState) => state?.user?.user);
    const { canCreate, canEdit, canView, canDelete, isAdmin } = usePermissions();
    const [coupon, setCoupon] = useState<ICoupon | null>(null);
    const [loading, setLoading] = useState(true);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const [couponOrders, setCouponOrders] = useState<IOrder[]>([]);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [orderPage, setOrderPage] = useState(1);
    const [orderPageMetadata, setOrderPageMetadata] = useState<IPageInfo>({ limit: 10, page: 1, total: 0, totalPages: 0 });

    const fetchCouponOrders = async (page = 1) => {
        if (!coupon?.id) return;
        setOrdersLoading(true);
        try {
            const response = await callApi<IOrderResponse>(
                `${API_URL}${API_ENDPOINT.orders}?couponId=${coupon?.id}&page=${page}&limit=10`,
                { method: 'GET' }
            );
            if (response?.data) {
                setCouponOrders(response?.data?.order || []);
                setOrderPageMetadata(response?.data?.pageInfo || { limit: 10, page: 1, total: 0, totalPages: 0 });
            }
        } catch (error) {
            console.error("Failed to fetch coupon orders", error);
        } finally {
            setOrdersLoading(false);
        }
    };

    const fetchCoupon = async () => {
        if (!id) return;
        setLoading(true);
        try {
            const response = await callApi<ISingleCouponResponse>(
                `${API_URL + API_ENDPOINT.coupons}/${id}`,
                { method: 'GET' }
            );
            setCoupon(response?.data);
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCoupon();
    }, [id]);

    useEffect(() => {
        fetchCouponOrders(orderPage);
    }, [coupon?.id, orderPage]);

    const handleDelete = async () => {
        if (!coupon) return;
        setDeleteLoading(true);
        try {
            const response = await callApi<any>(
                `${API_URL}${API_ENDPOINT.coupons}/${coupon.id}`,
                { method: 'DELETE' }
            );
            showToastNotification('success', response?.message || 'Coupon removed successfully');
            navigate(RoutesLink.Coupons);
        } catch (error) {
            ThrowError(error);
        } finally {
            setDeleteLoading(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-[60vh]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
                </div>
            </Layout>
        );
    }

    if (!coupon) {
        return (
            <Layout>
                <NoDataFound
                    title="Coupon Not Found"
                    description="The coupon you are looking for does not exist or may have been removed."
                    onAction={() => navigate(-1)}
                />
            </Layout>
        );
    }

    const discountDisplay =
        coupon.type === 'percentage'
            ? `${coupon.discountValue}%`
            : formatRand(coupon.discountValue);

    const usageLimitLabel = (val: number | null) =>
        val === null ? 'Unlimited' : String(val);

    const orderColumns: TableColumn<IOrder>[] = [
        {
            key: 'orderId',
            header: 'Order',
            render: (item) => (
                <div className="flex flex-col">
                    <span className="font-bold text-primary">#{item.orderId}</span>
                    <span className="text-[10px] text-muted-foreground">{formatDateTime(item.createdAt)}</span>
                </div>
            ),
        },
        {
            key: 'user',
            header: 'Customer',
            render: (item) => (
                <div className="flex flex-col">
                    <span className="font-medium text-foreground">{item.user?.fullName || '---'}</span>
                    <span className="text-xs text-muted-foreground">
                        {item.user?.phone}
                    </span>
                </div>
            )
        },
        {
            key: 'finalAmount',
            header: 'Total',
            render: (item) => (
                <div className="flex flex-col">
                    <span className="font-bold text-foreground">{formatRand(item.finalAmount)}</span>
                    {Number(item.discountAmount) > 0 && (
                        <span className="text-[10px] text-green-600">−{formatRand(item.discountAmount)} off</span>
                    )}
                </div>
            ),
        },
        {
            key: 'status',
            header: 'Status',
            render: (item) => <StatusBadge status={item.status} />,
        },
    ];

    return (
        <>
            <Layout>
                <div className="space-y-8 pb-20 animate-in fade-in slide-in-from-bottom-4 duration-700">

                    {/* ── Header ── */}
                    <MainHeading
                        title={coupon.name}
                        description={
                            <div className="flex items-center gap-2 text-muted-foreground/80 mt-1">
                                <Calendar className="w-3.5 h-3.5" />
                                <span className="text-sm">Created {formatDateTime(coupon.createdAt)}</span>
                            </div>
                        }
                        showBack
                        onBack={() => navigate(-1)}
                        actionLabel={(canEdit(RESOURCES.COUPON) && coupon.userId === user?.id) ? "Edit Coupon" : ""}
                        actionIcon={(canEdit(RESOURCES.COUPON) && coupon.userId === user?.id) && <PencilIcon className="w-4 h-4" />}
                        onAction={() => roleNavigate(RoutesLink.EditCoupon.replace(':id', coupon.code))}
                    >
                        {(canDelete(RESOURCES.COUPON) && coupon.userId === user?.id) && <button
                            onClick={() => setShowDeleteModal(true)}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-destructive border border-destructive/30 rounded-xl hover:bg-destructive hover:text-white transition-all cursor-pointer"
                        >
                            <Trash2 className="w-4 h-4" />
                            Delete
                        </button>}
                    </MainHeading>

                    {/* ── Coupon Code Hero Banner ── */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-primary/5 border border-primary/20 rounded-2xl px-6 py-5">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-primary/10 rounded-2xl">
                                <Ticket className="w-7 h-7 text-primary" />
                            </div>
                            <div>
                                <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-1">
                                    Coupon Code
                                </p>
                                <p className="text-2xl font-black tracking-[0.2em] text-primary uppercase">
                                    {coupon.code}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="text-right">
                                <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-1">
                                    Discount
                                </p>
                                <p className="text-3xl font-black text-primary">{discountDisplay}</p>
                            </div>
                            <StatusBadge status={coupon.status} />
                        </div>
                    </div>

                    {/* ── Main Grid ── */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">

                        {/* Left — 2 columns */}
                        <div className="lg:col-span-2 space-y-8">

                            {/* Basic Info */}
                            <SectionCard title="Basic Information" icon={<Info />}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-1">
                                    <DetailItem
                                        label="Campaign Name"
                                        value={coupon.name}
                                        icon={<Tag className="w-4 h-4" />}
                                        className="bg-secondary/5 rounded-2xl p-4 border border-transparent hover:border-primary/10 transition-all"
                                    />
                                    <DetailItem
                                        label="Discount Type"
                                        value={
                                            coupon.type === 'percentage'
                                                ? 'Percentage (%)'
                                                : 'Flat Rate (R)'
                                        }
                                        icon={<Percent className="w-4 h-4" />}
                                        className="bg-secondary/5 rounded-2xl p-4 border border-transparent hover:border-primary/10 transition-all"
                                    />
                                    <DetailItem
                                        label="Discount Value"
                                        value={discountDisplay}
                                        icon={<Tag className="w-4 h-4" />}
                                        className="bg-secondary/5 rounded-2xl p-4 border border-transparent hover:border-primary/10 transition-all"
                                    />
                                    <DetailItem
                                        label="Max Discount"
                                        value={coupon.maxDiscount !== null ? formatRand(coupon.maxDiscount) : 'No Limit'}
                                        icon={<Hash className="w-4 h-4" />}
                                        className="bg-secondary/5 rounded-2xl p-4 border border-transparent hover:border-primary/10 transition-all"
                                    />
                                    <DetailItem
                                        label="Min Purchase Value"
                                        value={formatRand(coupon.minimumValue)}
                                        icon={<Tag className="w-4 h-4" />}
                                        className="bg-secondary/5 rounded-2xl p-4 border border-transparent hover:border-primary/10 transition-all"
                                    />
                                    {coupon.description && (
                                        <div className="md:col-span-2 bg-secondary/5 rounded-2xl p-4 border border-transparent hover:border-primary/10 transition-all">
                                            <p className="text-[11px] uppercase tracking-widest text-muted-foreground/60 font-semibold mb-2">
                                                Description
                                            </p>
                                            <p className="text-sm text-foreground leading-relaxed">
                                                {coupon.description}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </SectionCard>

                            {/* Validity Period */}
                            <SectionCard title="Validity Period" icon={<Calendar />}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-1">
                                    <DetailItem
                                        label="Start Date"
                                        value={formatDateTime(coupon.startDate)}
                                        icon={<Calendar className="w-4 h-4" />}
                                        className="bg-secondary/5 rounded-2xl p-4 border border-transparent hover:border-primary/10 transition-all"
                                    />
                                    <DetailItem
                                        label="Expiry Date"
                                        value={formatDateTime(coupon.expirationDate)}
                                        icon={<AlarmClock className="w-4 h-4" />}
                                        className="bg-secondary/5 rounded-2xl p-4 border border-transparent hover:border-primary/10 transition-all"
                                    />
                                </div>
                            </SectionCard>

                            {/* Usage Limits */}
                            <SectionCard title="Usage Limits" icon={<Users />}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-1">
                                    <DetailItem
                                        label="Max Total Usage"
                                        value={usageLimitLabel(coupon.maxTotalUsage)}
                                        icon={<Hash className="w-4 h-4" />}
                                        className="bg-secondary/5 rounded-2xl p-4 border border-transparent hover:border-primary/10 transition-all"
                                    />
                                    <DetailItem
                                        label="Max Usage Per User"
                                        value={usageLimitLabel(coupon.maxUsagePerUser)}
                                        icon={<Users className="w-4 h-4" />}
                                        className="bg-secondary/5 rounded-2xl p-4 border border-transparent hover:border-primary/10 transition-all"
                                    />
                                    <DetailItem
                                        label="Max Usage Per Day"
                                        value={usageLimitLabel(coupon.maxUsagePerDay)}
                                        icon={<Clock className="w-4 h-4" />}
                                        className="bg-secondary/5 rounded-2xl p-4 border border-transparent hover:border-primary/10 transition-all"
                                    />
                                    <DetailItem
                                        label="Max Usage Per User/Day"
                                        value={usageLimitLabel(coupon.maxUsagePerUserPerDay)}
                                        icon={<Clock className="w-4 h-4" />}
                                        className="bg-secondary/5 rounded-2xl p-4 border border-transparent hover:border-primary/10 transition-all"
                                    />
                                </div>
                            </SectionCard>

                            {/* Applicability */}
                            {/* <SectionCard
                            title="Applicability"
                            icon={<Layers />}
                            extra={
                                <span className="text-[10px] font-bold py-1 px-3 bg-primary/10 text-primary rounded-full">
                                    {coupon.categoryIds?.length
                                        ? `${coupon.categoryIds.length} Categories`
                                        : 'All Categories'}
                                </span>
                            }
                        >
                            <div className="p-1">
                                {coupon.categoryIds && coupon.categoryIds.length > 0 ? (
                                    <div className="flex flex-wrap gap-2">
                                        {coupon.categoryIds.map((catId) => (
                                            <span
                                                key={catId}
                                                className="px-3 py-1.5 bg-primary/10 text-primary text-xs font-semibold rounded-xl border border-primary/20"
                                            >
                                                {catId}
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-muted-foreground italic">
                                        This coupon applies to all categories (no restriction).
                                    </p>
                                )}
                            </div>
                        </SectionCard> */}
                        </div>

                        {/* Right Sidebar */}
                        <div className="space-y-8 sticky top-6">

                            {/* Status Card */}
                            <SectionCard title="Coupon Status" icon={<Shield />}>
                                <div className="grid grid-cols-1 gap-y-6">
                                    <DetailItem
                                        label="Status"
                                        value={<StatusBadge status={coupon.status} />}
                                        icon={<Shield />}
                                    />
                                    <div className="pt-2 border-t border-border/40">
                                        <DetailItem
                                            label="Created On"
                                            value={formatDateTime(coupon.createdAt)}
                                            icon={<Calendar />}
                                        />
                                    </div>
                                    <DetailItem
                                        label="Last Updated"
                                        value={formatDateTime(coupon.updatedAt)}
                                        icon={<Calendar />}
                                    />
                                </div>
                            </SectionCard>

                            {/* Quick Stats */}
                            <SectionCard title="Quick Stats" icon={<Hash />}>
                                <div className="grid grid-cols-1 gap-4">
                                    <div className="flex items-center justify-between bg-secondary/5 rounded-2xl p-4">
                                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                            Discount
                                        </span>
                                        <span className="text-xl font-black text-primary">{discountDisplay}</span>
                                    </div>
                                    <div className="flex items-center justify-between bg-secondary/5 rounded-2xl p-4">
                                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                            Min. Order
                                        </span>
                                        <span className="text-xl font-black text-foreground">
                                            {formatRand(coupon.minimumValue)}
                                        </span>
                                    </div>
                                    {coupon.maxDiscount !== null && (
                                        <div className="flex items-center justify-between bg-secondary/5 rounded-2xl p-4">
                                            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                                Max Off
                                            </span>
                                            <span className="text-xl font-black text-foreground">
                                                {formatRand(coupon.maxDiscount)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </SectionCard>
                        </div>
                    </div>

                    {/* ── Coupon Usage (Orders) ── */}
                    <div className="mt-8">
                        <SectionCard title="Coupon Usage Summary" icon={<ShoppingBag />}>
                            <div className="p-4">
                                <CommonTable
                                    columns={orderColumns}
                                    data={couponOrders}
                                    loading={ordersLoading}
                                    actions={[
                                        ...(canView(RESOURCES.ORDER) ? [{
                                            text: 'View Order Details',
                                            icon: <Eye strokeWidth={1.5} size={16} className="cursor-pointer" />,
                                            onClick: (row: IOrder) => roleNavigate(RoutesLink.ViewOrders.replace(':id', row.id)),
                                        }] : []),
                                        ...(isAdmin ? [{
                                            text: 'View Customer',
                                            icon: <User strokeWidth={1.5} size={16} className="cursor-pointer" />,
                                            onClick: (row: IOrder) => roleNavigate(RoutesLink.CustomerDetails.replace(':id', row.userId || '')),
                                        }] : [])
                                    ]}
                                    emptyMessage="This coupon has not been used in any orders yet."
                                />

                                {orderPageMetadata.totalPages > 1 && (
                                    <div className="mt-4">
                                        <Pagination
                                            currentPage={orderPage}
                                            totalPages={orderPageMetadata.totalPages}
                                            totalItems={orderPageMetadata.total}
                                            itemsPerPage={orderPageMetadata.limit}
                                            onPageChange={(page) => setOrderPage(page)}
                                        />
                                    </div>
                                )}
                            </div>
                        </SectionCard>
                    </div>
                </div>
            </Layout>

            {/* ── Delete Confirmation Modal ── */}
            <ConfirmationModal
                open={showDeleteModal}
                onOpenChange={setShowDeleteModal}
                onConfirm={handleDelete}
                loading={deleteLoading}
                type="danger"
                title="Delete Coupon?"
                message={`Are you sure you want to permanently delete "${coupon.name}" (${coupon.code})? This action cannot be undone.`}
                confirmText="Yes, Delete"
                cancelText="Cancel"
                size="lg"
            />
        </>
    );
};
